import * as XLSX from 'xlsx-js-style';
import { VisualizationData, VisualizationTrace } from '../types/visualization.types';

function traceToRows(trace: VisualizationTrace): Record<string, unknown>[] {
  if (trace.type === 'pie' && trace.labels && trace.values) {
    return trace.labels.map((label, i) => ({
      Label: label,
      Value: trace.values![i] ?? '',
    }));
  }

  if (trace.x && trace.y) {
    return trace.x.map((x, i) => ({
      X: x,
      [trace.name]: trace.y![i] ?? '',
    }));
  }

  return [];
}

/**
 * Converts VisualizationData to an Excel workbook and triggers a download.
 *
 * When `rawData` is present the rows are written as-is so every original
 * column (Company, Profit Center, Value, …) appears in the spreadsheet.
 * Otherwise each trace becomes its own sheet.
 */
export function exportVisualizationToExcel(
  data: VisualizationData,
  fileName = 'export',
): void {
  const wb = XLSX.utils.book_new();

  // ── Raw tabular data (preferred for Excel exports) ──────────────────
  if (data.rawData && data.rawData.length > 0) {
    const ws = XLSX.utils.json_to_sheet(data.rawData);
    const sheetName = (data.title || 'Data').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  } else {
    // ── Trace-based fallback ────────────────────────────────────────────
    data.traces.forEach((trace) => {
      const rows = traceToRows(trace);
      if (rows.length === 0) return;

      const ws = XLSX.utils.json_to_sheet(rows);
      // Excel sheet names are limited to 31 characters
      const sheetName = (trace.name || 'Sheet').slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });
  }

  if (wb.SheetNames.length === 0) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([['No data available']]),
      'Sheet1',
    );
  }

  XLSX.writeFile(wb, `${fileName}.xlsx`);
}
